import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import axios from "axios";
import type { Set } from "./set";
import { RootState } from "../../store";
import { enhancedApiClient, isSuccessResponse, ApiResponse } from "../../services/enhancedApiClient";

interface LocationSlice {
  sets: Set[];
  formData: Partial<Set>;
  loading: boolean;
  error: string | null;
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  } | null;
}

const initialState: LocationSlice = {
  sets: [],
  formData: { setName: "", status: 1 },
  loading: false,
  error: null,
  pagination: null,
};

//get list
export const fetchSets = createAsyncThunk(
  "set/fetchSets",
  async (params: { page?: number; limit?: number; search?: string } = {}, { rejectWithValue }) => {
    try {
      const queryParams = new URLSearchParams();
      if (params?.page) queryParams.append('page', params.page.toString());
      if (params?.limit) queryParams.append('limit', params.limit.toString());
      if (params?.search) queryParams.append('search', params.search);
      const url = `/master/set${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
      const response = await enhancedApiClient.get(url);
      if (isSuccessResponse(response)) {
        console.log("Fetch set response:", response.data);
        return response.data;
      } else {
        return rejectWithValue("Failed to fetch sets");
      }
    } catch (error: any) {
      console.error("Fetch sets error:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Failed to fetch sets";
      return rejectWithValue(errorMessage);
    }
  }
);

// ✅ Create set
export const createSet = createAsyncThunk(
  "set/createSet",
  async (data: Partial<Set>, { rejectWithValue }) => {
    try {
      const response = await enhancedApiClient.post("/master/set", data);

      if (isSuccessResponse(response)) {
        return response.data;
      } else {
        return rejectWithValue("Failed to create set");
      }
    } catch (error: any) {
      console.error("Create set error:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Failed to create color";
      return rejectWithValue(errorMessage);
    }
  }
);

// ✅ Edit set
export const editSet = createAsyncThunk(
  "set/editSet",
  async ({ id, data }: { id: number; data: Partial<Set> }, { rejectWithValue }) => {
    try {
      const response = await enhancedApiClient.put(`/master/set/${id}`, data);

      if (isSuccessResponse(response)) {
        return response.data;
      } else {
        return rejectWithValue("Failed to update set");
      }
    } catch (error: any) {
      console.error("Edit set error:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Failed to update set";
      return rejectWithValue(errorMessage);
    }
  }
);

// ✅ Toggle set status
export const toggleSetStatus = createAsyncThunk(
  "set/toggleSetStatus",
  async ({ id, status }: { id: number; status: number }, { rejectWithValue }) => {
    try {
      const response = await enhancedApiClient.patch(`/master/set/${id}/status`, { status });

      if (isSuccessResponse(response)) {
        return response.data;
      } else {
        return rejectWithValue("Failed to toggle set status");
      }
    } catch (error: any) {
      console.error("Toggle set status error:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Failed to toggle set status";
      return rejectWithValue(errorMessage);
    }
  }
);

export const setSlice = createSlice({
  name: "set",
  initialState,
  reducers: {
    setSetName: (state, action: PayloadAction<string>) => {
      state.formData.setName = action.payload;
    },
    setSetStatus: (state, action: PayloadAction<number>) => {
      state.formData.status = action.payload;
    },
    resetForm: (state) => {
      state.formData = { setName: "", status: 1 };
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch
      .addCase(fetchSets.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSets.fulfilled, (state, action) => {
        state.loading = false;
        state.sets = action.payload.records;
        state.pagination = action.payload.pagination;
        console.log("sets fetched successfully:", action.payload);
      })
      .addCase(fetchSets.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Create
      .addCase(createSet.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createSet.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        if(action.payload) {
          const newLocation: Set = {
            id: action.payload.setId,
            setId: action.payload.setId,
            setName: action.payload.setName,
            status: action.payload.status,
            updated_at: action.payload.updatedOn,
            updated_by: action.payload.updatedBy,
            action:action.payload.action,
          };
          state.sets.unshift(newLocation);
          }
        state.formData = { setName: "", status: 1 };
      })
      .addCase(createSet.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Edit
      .addCase(editSet.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(editSet.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        const updatedSet : Set = {
          id: action.payload.setId,
          setId: action.payload.setId,
          setName: action.payload.setName,
          status: action.payload.status,
          updated_at: action.payload.updatedOn,
          updated_by: action.payload.updatedBy,
          action:action.payload.action,
        }
        const index = state.sets.findIndex(s => s.id === updatedSet.id);
        if (index !== -1) {
          state.sets[index] = updatedSet;
        }
        state.formData = { setName: "", status: 1 };
      })
      .addCase(editSet.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Toggle Status
      .addCase(toggleSetStatus.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(toggleSetStatus.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        const updatedSet : Set = {
          id: action.payload.setId,
          setId: action.payload.setId,
          setName: action.payload.setName,
          status: action.payload.status,
          updated_at: action.payload.updatedOn,
          updated_by: action.payload.updatedBy,
          action: action.payload.action,
        }
        const index = state.sets.findIndex(s => s.setId === updatedSet.setId);
        if (index !== -1) {
          state.sets = [
            ...state.sets.slice(0, index),
            updatedSet,
            ...state.sets.slice(index + 1)
          ];
        }
      })
      .addCase(toggleSetStatus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { setSetName, setSetStatus, resetForm } = setSlice.actions;
// ✅ Selectors
export const selectedSets = (state: RootState) => state.set.sets;
export const selectSetLoading = (state: RootState) => state.set.loading;
export const selectSetError = (state: RootState) => state.set.error;
export const selectSetnPagination = (state: RootState) => state.set.pagination

export default setSlice.reducer;
